/* ---------------------------------------------------------------------- */
/* HomeScreen.jsx                              */
/* ---------------------------------------------------------------------- */
import React, { useState, useEffect } from "react";
import axios from "axios";
import CreateQuest from "./CreateQuest";
import CharacterEquipmentScreen from "./CharacterEquipmentScreen";
import DungeonScreen from './DungeonScreen';
import MyScreen from './MyScreen';
import {
  FiTrash2,
  FiEdit,
  FiAlertCircle,
  FiChevronLeft,
  FiChevronRight,
  FiChevronDown
} from "react-icons/fi";
import { getCalendar } from "./helper";

/* ====================== 탭 SVG (ReactComponent) ====================== */
// 1)  public/assets ➜ src/assets  로 옮긴 뒤
// 2)  SVG 내부 <svg> 태그의 fill/stroke 를  fill="currentColor" 로 교체해 두면
//     부모에서 color 만 주입해도 자동으로 색이 바뀝니다.
import { ReactComponent as HomeLight }  from "./assets/home-light.svg";
import { ReactComponent as HomeDark }   from "./assets/home-dark.svg";
import { ReactComponent as DungeonLight } from "./assets/dungeon-light.svg";
import { ReactComponent as DungeonDark }  from "./assets/dungeon-dark.svg";
import { ReactComponent as MyLight }     from "./assets/my-light.svg";
import { ReactComponent as MyDark } from "./assets/my-dark.svg";


import { ReactComponent as SwordLight } from "./assets/sword-light.svg";
import { ReactComponent as SwordDark }  from "./assets/sword-dark.svg";


/* ====================== 다크 / 라이트 ====================== */
const COLOR_SET = {
    light: {
      bg: "#F8F6F2",
      card: "#FFFFFF",
      primary: "#DBD1BD",
      text: "#333333",
      gray: "#888888",
    divider: "#E3E4E9",
    createBtn: "#DBD1BD",
    selectedBtn: "#DBD1BD",
    unSelectedBtn: "#F8F6F2",
    selectedWeek: "#DBD1BD",
    selectedNum: "#FFFFFF",
    },
    dark: {
      bg: "#1A1A2E",
      card: "#1E3A52",
      primary: "#FFB627",
      text: "#FFFFFF",
      gray: "#AAAAAA",
      divider: "#36363D",
      createBtn: "#22C4B6BF",
      selectedBtn: "#9DCAC9",
      unSelectedBtn: "#1E3A52",
      selectedWeek: "#000000",
      selectedNum: "#9DCAC9",
    },
  };

/* ====================== 레벨별 필요 경험치 ====================== */
// 서버의 levelConfig(requiredExp)와 동일하게 유지해야 합니다.
const LEVEL_CONFIG = [
  /* Lv1→2 */ 5,
  /* Lv2→3 */ 10,
  /* Lv3→4 */ 15,
  /* Lv4→5 */ 20,
  /* Lv5→6 */ 30,
  /* Lv6→7 */ 50,
  /* Lv7→8 */ 80,
  /* Lv8→9 */ 100,
  /* Lv9→10*/ 130,
  /* Lv10→11*/ 130,
  /* Lv11→12*/ 130,
  /* Lv12→13*/ 130,
  /* Lv13→14*/ 130,
  /* Lv14→15*/ 130,
  /* Lv15→16*/ 180,
  /* Lv16→17*/ 210,
  /* Lv17→18*/ 240,
  /* Lv18→19*/ 280,
  /* Lv19→20*/ 320,
];

// ✔️ 체크 1회당 경험치 증감량 (서버와 동일하게 5로 고정)
const EXP_GAIN = 5;




/* ==================== 환경변수 & axios 인스턴스 ==================== */
const API_URL = process.env.REACT_APP_API_URL;
const api = axios.create({ baseURL: API_URL });

/* accessToken 헤더 부착 */
api.interceptors.request.use((cfg) => {
  const tk = localStorage.getItem("accessToken");
  console.log("[API Request] JWT 토큰:", tk);
  if (tk) cfg.headers.Authorization = `Bearer ${tk}`;
  return cfg;
});

/* 401 갱신(동일 로직) */
api.interceptors.response.use(
  (res) => res,
  async (err) => {
    if (err.response?.status !== 401 || err.config._retry) throw err;
    err.config._retry = true;
    const rt = localStorage.getItem("refreshToken");
    if (!rt) throw err;
    try {
      const { data } = await axios.post(`${API_URL}/auth/token`, { refreshToken: rt });
      localStorage.setItem("accessToken", data.token);
      err.config.headers.Authorization = `Bearer ${data.token}`;
      return api(err.config);
    } catch {
      localStorage.clear();
      window.location.reload();
      throw err;
    }
  }
);

/* ------------------------- 상수 ------------------------- */
const weekDays = ["일", "월", "화", "수", "목", "금", "토"];
const weekDaysForButton = ["일", "월", "화", "수", "목", "금", "토"];

//===========================================================================
//  UTIL: 경험치 델타 반영하여 프로필 계산
//===========================================================================
function applyExpChange(profile, delta) {
  // 복사본 생성
  let level = profile.level;
  let exp = profile.exp + delta;
  // 레벨업 처리
  while (exp >= (LEVEL_CONFIG[level - 1] ?? Infinity)) {
    exp -= LEVEL_CONFIG[level - 1];
    level += 1;
  }
  // 레벨다운 처리
  while (exp < 0 && level > 1) {
    level -= 1;
    exp += LEVEL_CONFIG[level - 1];
  }
  return { ...profile, level, exp };
}

/* ------------------------------ 팝업 메뉴 ----------------------------- */
function PopupMenu({ C, x, y, onClose, onDelete, onEdit }) {
  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.3)",
          zIndex: 1000,
        }}
      />
      <div
        style={{
          position: "absolute",
          top: y,
          left: x,
          transform: "translate(-50%, 0)",
          background: C.card,
          borderRadius: 12,
          boxShadow: "0 2px 16px rgba(0,0,0,0.08)",
          width: 200,
          zIndex: 1001,
          overflow: "hidden",
        }}
      >
        <button
          onClick={onDelete}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            padding: "12px 16px",
            border: "none",
            background: "none",
            color: "#F25C5C",
            fontWeight: 600,
            fontSize: 16,
            cursor: "pointer",
          }}
        >
          삭제하기
          <FiTrash2 color="#888" size={20} />
        </button>
        <div style={{ height: 1, background: "#E3E4E9" }} />
        <button
          onClick={onEdit}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
            padding: "12px 16px",
            border: "none",
            background: "none",
            color: "#333",
            fontWeight: 600,
            fontSize: 16,
            cursor: "pointer",
          }}
        >
          수정하기
          <FiEdit color="#888" size={20} />
        </button>
      </div>
    </>
  );
}

/* --------------------------- 삭제 확인창 --------------------------- */
function ConfirmDialog({ C, onClose, onConfirm, skipNext, setSkipNext }) {
  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.3)",
          zIndex: 2000,
        }}
      />
      <div
        style={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%,-50%)",
          background: C.card,
          borderRadius: 12,
          border: "2px solid #333",
          width: 300,
          padding: 24,
          zIndex: 2001,
          textAlign: "center",
        }}
      >
        <FiAlertCircle size={48} color="#F25C5C" style={{ marginBottom: 12 }} />
        <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 24 }}>
          삭제하시겠습니까?
        </div>
        <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
          <button
            onClick={onClose}
            style={{
              flex: 1,
              padding: 8,
              border: "2px solid #333",
              borderRadius: 8,
              background: "none",
              fontWeight: 600,
            }}
          >
            아니오
          </button>
          <button
            onClick={onConfirm}
            style={{
              flex: 1,
              padding: 8,
              border: "none",
              borderRadius: 8,
              background: "#F25C5C",
              color: C.card,
              fontWeight: 600,
            }}
          >
            예
          </button>
        </div>
        <label style={{ fontSize: 14, color: "#888" }}>
          <input
            type="checkbox"
            checked={skipNext}
            onChange={(e) => setSkipNext(e.target.checked)}
            style={{ marginRight: 6 }}
          />
          다음부터 이 창 띄우지 않기
        </label>
      </div>
    </>
  );
}

/* ===================================================================== */
/* MAIN VIEW                               */
/* ===================================================================== */
export default function HomeScreen({ user }) {


/* ---------- 테마 상태 ---------- */
  const [theme, setTheme] = useState(
    localStorage.getItem("theme") || "dark"
  );
  const C = COLOR_SET[theme];        // ★ 항상 C로 색상 접근
  const SwordIcon = theme === "dark" ? SwordDark : SwordLight;
/* ====================== 탭 정의 ====================== */
  const tabs = [
    {
      key: "home",
      label: "홈",
      Icon: theme === "dark" ? HomeDark : HomeLight,
    },
    {
      key: "dungeon",
     label: "던전",
      Icon: theme === "dark" ? DungeonDark : DungeonLight,
    },
    {
      key: "my",
      label: "마이",
      Icon: theme === "dark" ? MyDark : MyLight,
    },
  ];

  /* MyScreen에서 theme-changed 이벤트가 오면 동기화 */
  useEffect(() => {
    const sync = () =>
      setTheme(localStorage.getItem("theme") || "dark");
    window.addEventListener("theme-changed", sync);
    sync();                          // 첫 진입에서도 실행
    return () => window.removeEventListener("theme-changed", sync);
  }, []);
  // --- 모든 Hook을 컴포넌트 최상단에 선언 ---
  const [activePage, setActivePage] = useState("home");
  const today = new Date();
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [selectedDate, setSelectedDate] = useState(today.getDate());
  
  const calendar = getCalendar(currentYear, currentMonth);
  const initialWeekIdx = calendar.findIndex((w) =>
    w.some(c => c.day === selectedDate && c.offset === 0) 
  );
  const [weekIndex, setWeekIndex] = useState(initialWeekIdx >= 0 ? initialWeekIdx : 0);
  
  const [routines, setRoutines] = useState([]);
  const [todos, setTodos] = useState([]);
  const [selectedTab, setSelectedTab] = useState("루틴");
  const [loading, setLoading] = useState(false);
  const [touchStartX, setTouchStartX] = useState(0);
  
  const sliderRef = React.useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [slideAnimated, setSlideAnimated] = useState(true);
  const [dragStartX, setDragStartX] = useState(null);
  const [scrollStart, setScrollStart] = useState(0);
  
  const onMouseDown = (e) => {
    setIsDragging(true);
    setDragStartX(e.clientX);
    setScrollStart(sliderRef.current.scrollLeft);
  };
  const onMouseMove = (e) => {
    if (!isDragging) return;
    const dx = dragStartX - e.clientX;
    sliderRef.current.scrollLeft = scrollStart + dx;
  };
  const onMouseUp = () => setIsDragging(false);
  const onMouseLeave = () => setIsDragging(false);

  const [showCreateQuest, setShowCreateQuest] = useState(false);
  const [showEquipment, setShowEquipment] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [menuState, setMenuState] = useState({ visible: false, x: 0, y: 0, item: null });
  const [showConfirm, setShowConfirm] = useState(false);
  const [editingQuest, setEditingQuest] = useState(null);
  const [deletingItem, setDeletingItem] = useState(null);
  const [skipNext, setSkipNext] = useState(false);
  
  // 문제가 되었던 Hook들을 다른 Hook들과 함께 위로 이동
  const [profile, setProfile] = useState(
    user?.profile ?? { level: 1, nickname: "Guest", job: "모험가", exp: 0 }
  );
  
  const selectedISO = `${currentYear}-${String(currentMonth + 1).padStart(2, "0")}-${String(
    selectedDate
  ).padStart(2, "0")}`;

  // 퀘스트 목록 재조회 함수
 const fetchQuests = async (iso = selectedISO) => {
   setLoading(true);
   try {
     const { data } = await api.get("/quest", { params: { date: iso } });
     const { routines, todos } = splitQuests(data);
     setRoutines(routines);
     setTodos(todos);
   } catch (e) {
     console.error("/quest GET error:", e.response ?? e);
     alert("퀘스트 불러오기에 실패했습니다");
   } finally {
     setLoading(false);
   }
 };

  // useEffect Hook들도 모두 위로 이동
   useEffect(() => { 
     fetchQuests();  
   }, [selectedISO]);

  useEffect(() => {
    if (activePage !== "home") return;
    api
      .get("/profile/me")
      .then(({ data }) => {
        setProfile(data.result);
      })
      .catch((err) => {
        console.error("프로필 재조회 실패:", err);
      });
  }, [activePage]);

  // ---------------------------------------------------------
  // ✅ 체크/해제: Optimistic + 서버 확정 후 프로필 재조회
  // ---------------------------------------------------------
  const toggleDone = async (instance) => {
    const wasDone = instance.isDone;
    const deltaExp = wasDone ? -EXP_GAIN : EXP_GAIN;
    const listSetter = selectedTab === "루틴" ? setRoutines : setTodos;

    // 1) 리스트 Optimistic Update
    listSetter((prev) => prev.map((it) => (it._id === instance._id ? { ...it, isDone: !wasDone } : it)));
    // 2) 프로필 Optimistic Update
    setProfile((prev) => applyExpChange(prev, deltaExp));

    try {
      // 2) PATCH 응답에 포함된 최신 profile을 바로 반영  
      const { data } = await api.patch(   
        `/quest/${instance.questId._id}/check`,  
        { isDone: !wasDone, date: selectedISO }  
      );   
      setProfile(data.profile);
    } catch (e) {
      console.error("toggleDone PATCH 실패:", e.response ?? e);
      listSetter((prev) => prev.map((it) => (it._id === instance._id ? { ...it, isDone: wasDone } : it)));
      setProfile((prev) => applyExpChange(prev, -deltaExp));
      alert("상태 변경에 실패했습니다");
    }
  };
  
  const splitQuests = (payload = {}) => {
    let routines = [], todos = [];
    if (payload.routines || payload.todos) {
      routines = payload.routines ?? [];
      todos = payload.todos ?? [];
      return { routines, todos };
    }
    if (payload.result?.routines || payload.result?.todos) {
      routines = payload.result.routines ?? [];
      todos = payload.result.todos ?? [];
      return { routines, todos };
    }
    const arr = Array.isArray(payload.result)
      ? payload.result
      : Array.isArray(payload)
      ? payload
      : [];
    if (!arr.length && payload.result && payload.result._id) arr.push(payload.result);
    if (!arr.length && payload._id) arr.push(payload);
    arr.forEach((inst) =>
      (inst.questId?.type === "루틴" ? routines : todos).push(inst)
    );
    return { routines, todos };
  };
  
  
/* ------------------------------------------------------------------ */
/* 공통 로직 추출                                                   */
/* ------------------------------------------------------------------ */
function moveWeek(dir /* -1:이전, 1:다음 */) {
    const wantedDow = new Date(currentYear, currentMonth, selectedDate).getDay();
  
    /* ---------------------------------- 달/주 인덱스 계산 ---------------------------------- */
    let y = currentYear, m = currentMonth, newWeekIdx = weekIndex;
    let cal = calendar;
  
    if (dir === -1) {                        // ◁ 이전 주
      if (weekIndex > 0) newWeekIdx--;
      else {
        const d = new Date(y, m - 1, 1);
        y = d.getFullYear(); m = d.getMonth();
        cal = getCalendar(y, m);
        newWeekIdx = cal.length - 1;
      }
    } else {                                 // ▷ 다음 주
      if (weekIndex < cal.length - 1) newWeekIdx++;
      else {
        const d = new Date(y, m + 1, 1);
        y = d.getFullYear(); m = d.getMonth();
        cal = getCalendar(y, m);
        newWeekIdx = 0;
      }
    }  
    /* -------------------------------- 같은 요일 셀 선택 -------------------------------- */
    let cell = cal[newWeekIdx][wantedDow];
    if (cell.offset !== 0) {
      // 같은 요일이 없으면(매우 드물지만) 그 주에서 offset===0인 첫 셀 사용
      cell = cal[newWeekIdx].find(c => c.offset === 0) || cell;
    }
  
    /* -------------------------------- 상태 반영 -------------------------------- */
    let finalY = y, finalM = m;
    if (cell.offset === -1) {                // 주 안에서도 다시 이전/다음 달일 수 있음
      const d = new Date(y, m - 1, cell.day);
      finalY = d.getFullYear(); finalM = d.getMonth();
    } else if (cell.offset === 1) {
      const d = new Date(y, m + 1, cell.day);
      finalY = d.getFullYear(); finalM = d.getMonth();
    }
  
    setCurrentYear(finalY);
    setCurrentMonth(finalM);
    setWeekIndex(newWeekIdx);
    setSelectedDate(cell.day);
}
 
  function prevWeek() { moveWeek(-1); }
  function nextWeek() { moveWeek(1); }

  const handleStart = (x) => setDragStartX(x);
  const handleEnd = (x) => {
    if (dragStartX === null) return;
    const dx = x - dragStartX;
    if (dx > 50) moveWeek(-1);
    else if (dx < -50) moveWeek(1);
    setDragStartX(null);
  };

const handleDateClick = ({ day, offset }) => {
   let y = currentYear, m = currentMonth + offset;
    if (m < 0)  { m = 11; y -= 1; }
  if (m > 11) { m = 0; y += 1; }
  const monthChanged = offset !== 0;
  if (monthChanged) setSlideAnimated(false); 

     setCurrentYear(y);
     setCurrentMonth(m);
     setSelectedDate(day);
     // 새 달의 달력 다시 계산해 놓고
     const newCal = getCalendar(y, m);
     const newWeekIdx = newCal.findIndex(w => w.some(d => d.day === day && d.offset === 0));
  if (newWeekIdx >= 0) setWeekIndex(newWeekIdx);

  if (monthChanged) requestAnimationFrame(() => setSlideAnimated(true)); // ➌ 다음 프레임에 다시 켜기
 };

  const prevMonth = () => {
    setCurrentMonth((m) => (m === 0 ? 11 : m - 1));
    if (currentMonth === 0) setCurrentYear((y) => y - 1);
    setSelectedDate(1);
  };
  const nextMonth = () => {
    setCurrentMonth((m) => (m === 11 ? 0 : m + 1));
    if (currentMonth === 11) setCurrentYear((y) => y + 1);
    setSelectedDate(1);
  };

  const handleItemClick = (item, e) => {
    e.stopPropagation();
    setMenuState({ visible: true, x: e.clientX, y: e.clientY, item });
  };

  const handleDeleteConfirm = async () => {
    if (!deletingItem) return;
    try {
      await api.delete(`/quest/${deletingItem.questId._id}`);
      const setter = selectedTab === "루틴" ? setRoutines : setTodos;
      setter(prev => prev.filter(it => it._id !== deletingItem._id));
    } catch (e) {
      console.error("DELETE /quest error", e.response ?? e);
      alert("삭제에 실패했습니다");
    } finally {
      setShowConfirm(false);
      setMenuState(s => ({ ...s, visible: false }));
      setDeletingItem(null);
    }
  };

  // --- 조건부 렌더링 (모든 Hook 선언 이후) ---
  if (showEquipment) {
    return <CharacterEquipmentScreen onBack={() => setShowEquipment(false)} />;
  }

  if (showCreateQuest) {
    return (
      <CreateQuest
        initialData={editingQuest}       
        onSaved={async () => {
          // 1) 모달 닫기
          setShowCreateQuest(false);
          setEditingQuest(null);
          // 2) 퀘스트 리로드
          await fetchQuests();
        }}
        onBack={() => {
          setShowCreateQuest(false);
          setEditingQuest(null);
        }}
      />
    );
  }
  
  if (activePage === "dungeon") {
    return <DungeonScreen onBack={() => setActivePage("home")} user={user} />;
  }

  if (activePage === "my") {
    return <MyScreen onBack={() => setActivePage("home")} user={user} />;
  }

  // --- 최종 렌더링 ---
  /* ---------------- 경험치 계산 수정 ---------------- */
  const requiredExp = LEVEL_CONFIG[profile.level - 1] ?? 1000; // 다음 레벨까지 필요 EXP
  const expWidth = `${Math.min((profile.exp / requiredExp) * 100, 100)}%`;
  const list = (selectedTab === "루틴" ? routines : todos)
  .filter(it => it.questId && it.questId._id);

    return (
        <div
          style={{
            background: C.bg,
            /* 반응형으로 변경 */
            width: "100%",
            maxWidth: 393,
            boxSizing: "border-box",
            minHeight: "100vh",
            margin: "0 auto",
            padding: 0,
            fontFamily: "'Pretendard','Spline Sans','SF Pro',sans-serif",
            display: "flex",
            flexDirection: "column",
          }}
       >
   {/* 상단: 카드 컨테이너와 버튼을 나란히 배치 */}
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: 16,
      }}
    >
  {/* 카드 컨테이너 (버튼 제외) */}
   <div
    style={{
      background: C.card,
      borderRadius: 12,
      padding: 16,
      flex: "0 0 auto",    // 화면에 꽉 차지 않고,
      width: "auto",        // 내용물만큼만 가로 크기를 가집니다.
      /* 필요하다면 maxWidth: 280 같은 최대 폭을 추가해도 좋아요 */
    }}
  >
  {/* 1) 레벨·닉네임 + 직업 (간격 띄우기) */}
 <div
   style={{
     display: "flex",
     alignItems: "center",
     gap: 8,           // 닉네임과 직업 사이 간격
     /* justifyContent: "flex-start"는 기본값이라 생략해도 됩니다 */
   }}
 >
   <span style={{ fontSize: 16, fontWeight: 700, color: C.text }}>
     Lv. {profile.level} {profile.nickname}
   </span>
   <span style={{ fontSize: 16, fontWeight: 700, color: C.text }}>
     {profile.job}
   </span>
 </div>

        {/* 2) 경험치 라벨 + 프로그레스 바 */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginTop: 12,
          }}
        >
          <span style={{ fontSize: 14, fontWeight: 600, color: C.text, marginRight: 8 }}>
            경험치
          </span>
          <div
            style={{
              flex: 1,
              height: 8,
              background: C.divider,
              borderRadius: 4,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                width: expWidth,
                height: "100%",
                  background: C.primary,
                 transition: "width 0.4s ease"
              }}
            />
          </div>
        </div>
      </div>

      {/* 카드 옆에 독립된 버튼 */}
      <button
      onClick={() => alert("서비스 준비중입니다")}
      style={{
        marginLeft: 12,
        width: 44,
        height: 44,
        background: "transparent",          // 배경 투명
        border: "2px solid #51CDBB",       // 테두리 색
        borderRadius: 12,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
      }}
    >
      <SwordIcon
        width={24}
        height={24}
        style={{ color: "#51CDBB" }}       // 아이콘도 테두리 색과 동일하게
      />
    </button>


    </div>
        

      {/* 캐릭터 이미지 */}
      <div
  style={{
    width: "calc(100% - 32px)",              // 좌우 꽉 채우기
    height: "100%",                // 기존 img 높이 유지
    margin: "0 16px 12px",  
    borderRadius: 12,
         
    backgroundImage: `url(/assets/town_background.png)`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  }}
>
  <img
    src={`/assets/${profile.characterType}.png`}
    alt="캐릭터"
    style={{
      height: "100%",
      width: "auto",
      objectFit: "contain",
    }}
  />
</div>

      {/* 인라인 캘린더 week-slider */}
      <div style={{ background: C.card, borderRadius: 20, padding: 16, margin: "0 16px 20px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div
            onClick={() => setShowCalendar(true)}
            style={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
            }}
          >
           <span style={{ fontWeight: 600, fontSize: 18, color: C.text }}>
              {currentYear}년 {currentMonth + 1}월
            </span>
            <FiChevronDown size={20} style={{ marginLeft: 6, color: C.text }} />
          </div>
        </div>
        <div
          style={{
            overflow: "hidden",      // 자유 스크롤 해제
            marginTop: 12,
            touchAction: "pan-y",    // 세로 스크롤만 허용
            userSelect: "none",
          }}
          onMouseDown={(e) => handleStart(e.clientX)}
          onMouseUp={(e)   => handleEnd(e.clientX)}
          onTouchStart={(e)=> handleStart(e.touches[0].clientX)}
          onTouchEnd={(e)  => handleEnd(e.changedTouches[0].clientX)}
        >
          <div
            style={{
              display: "flex",
              width: `${calendar.length * 100}%`,
              transform: `translateX(-${weekIndex * (100 / calendar.length)}%)`,
              transition: slideAnimated ? "transform 0.3s ease" : "none",
            }}
          >
            {calendar.map((week, i) => (
              <div
                key={i}
                style={{ flex: `0 0 ${100 / calendar.length}%`, display: "flex", justifyContent: "space-between" }}
              >
                {weekDaysForButton.map((d, idx) => {
                  const cell   = week[idx];                // {day, offset}
                  const dayNum = cell?.day;
                  const sel    = cell && cell.offset === 0 && selectedDate === dayNum;
                  return (
                    <div
                      key={d}
                      onClick={() => cell && handleDateClick(cell)}
                      style={{
                        width: 36,
                        padding: "4px 0",
                        cursor: dayNum ? "pointer" : "default",
                        background: sel ? C.selectedWeek : "transparent",
                        borderRadius: 12,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                      }}
                    >
                      <span style={{ fontSize: 12, color: sel ? C.text : C.gray, marginBottom: 4 }}>{d}</span>
                      <div
                        style={{
                          width: 30,
                          height: 30,
                          background: sel ? C.selectedNum : "transparent",
                          borderRadius: 12,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <span style={{ fontSize: 16, fontWeight: sel ? 700 : 500, color: sel ? "#333" : "#888" }}>
                          {dayNum || ""}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 루틴/투두 탭 & 리스트 */}
      <div style={{ padding: "0 16px", flex: 1, overflowY: "auto" }}>
        <div
          style={{
            display: "flex",
            gap: 16,
            background: C.card,
            borderRadius: 12,
            padding: 8,
            marginBottom: 12,
          }}
        >
          {["루틴", "투두"].map((t) => (
            <button
              key={t}
              onClick={() => setSelectedTab(t)}
              style={{
                flex: 1,
                background: selectedTab === t ? C.selectedBtn : C.unSelectedBtn,
                border: "none",
                borderRadius: 8,
                padding: 8,
                fontWeight: 700,
                fontSize: 15,
                color: selectedTab === t ? "#333" : "#888",
              }}
            >
              {t}
            </button>
          ))}
        </div>
        {loading ? (
          <div style={{ textAlign: "center", padding: 20 , color: C.text}}>불러오는 중...</div>
        ) : list.length === 0 ? (
          <div style={{ textAlign: "center", padding: 20, color: C.text }}>
            퀘스트가 없습니다
          </div>
        ) : (
          list.map((instance) => (
           /* ───────── 교체할 JSX ───────── */
<div
  key={instance.questId._id}
  onClick={(e) => handleItemClick(instance, e)}
  style={{
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    background: C.card,
    borderRadius: 12,
    padding: "12px 16px",
    marginBottom: 8,
    cursor: "pointer",
  }}
>
  {/* 제목 + 내용 묶음 */}
  <div style={{ flex: 1, marginRight: 12 }}>
    {/* 제목 */}
    <span
      style={{
        fontSize: 15,
        fontWeight: 700,
        color: C.text,   
        display: "block",
        marginBottom: 4,
      }}
    >
      {instance.questId?.title || "(제목 없음)"}
    </span>

    {/* 내용: 두 줄까지 표시 */}
    <span
      style={{
        fontSize: 13,
        color: C.gray, 
        lineHeight: 1.35,
        overflow: "hidden",
        textOverflow: "ellipsis",
        display: "-webkit-box",
        WebkitLineClamp: 2,           // 최대 두 줄
        WebkitBoxOrient: "vertical",
      }}
    >
      {instance.questId?.description || "(내용 없음)"}
    </span>
  </div>

  {/* 완료 체크박스 */}
  <input
    type="checkbox"
    checked={instance.isDone}
    onClick={(e) => e.stopPropagation()}
    onChange={() => toggleDone(instance)}
    style={{ width: 20, height: 20 }}
  />
</div>
/* ───────── 교체 끝 ───────── */

          ))
        )}
      </div>

      {/* 퀘스트 생성 */}
      <button
       onClick={() => {
           // 홈에서 선택된 ISO 날짜를 initialData.date 로 넘겨 줍니다.
           setEditingQuest({ date: selectedISO });
           setShowCreateQuest(true);
         }}
        style={{
          margin: "0 16px 12px",
          width: "calc(100% - 32px)",
          padding: 16,
          background: theme === "dark" ? "#22C4B6BF" : C.createBtn,  // 다크모드일 땐 #22C4B6BF
          color:     theme === "dark" ? "#FFFFFF"   : C.text,
          border: "none",
          borderRadius: 12,
          fontWeight: 700,
          fontSize: 16,
        }}
      >
        퀘스트 생성하기
      </button>

       {/* 하단 탭바 */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-around",
          alignItems: "center",
          height: 56,
          background: C.bg,
          borderTop: `1px solid ${C.divider}`,
        }}
      >
         {tabs.map((t) => {
    const isActive = activePage === t.key;
    const tabColor = isActive ? C.text : C.gray;
    const Icon = t.Icon;
    return (
     <div
       key={t.key}
       onClick={() => setActivePage(t.key)}
      style={{
        display: "flex",
         flexDirection: "column",   // 아이콘 위, 텍스트 아래
         alignItems: "center",
         justifyContent: "center",
         cursor: "pointer",
         width: 56,                 // 넓이를 지정해주면 고르게 배치됨
       }}
     >
       <Icon width={24} height={24} style={{ color: tabColor }} />
      <span
         style={{
           marginTop: 4,
           fontSize: 11,
           color: tabColor,
           lineHeight: 1,
         }}
       >
         {t.label}
       </span>
     </div>
    );
  })}
      </div>

      {/* 팝업 & 확인창 */}
      {menuState.visible && (
        <PopupMenu
          C={C}
          {...menuState}
          onClose={() => setMenuState({ ...menuState, visible: false })}
          onDelete={() => {
            setDeletingItem(menuState.item);
            setShowConfirm(true)
          }}
          onEdit={() => {
            setEditingQuest(menuState.item.questId);
            setShowCreateQuest(true);
            setMenuState(s => ({ ...s, visible: false }));
          }}
        />
      )}
      {showConfirm && (
        <ConfirmDialog
         C={C}
         onClose={() => {
          setShowConfirm(false);
          setDeletingItem(null);
          }}
          onConfirm={handleDeleteConfirm}
          skipNext={skipNext}
          setSkipNext={setSkipNext}
        />
      )}

      {/* 달력 BottomSheet */}
      {showCalendar && (
        <>
          <div
            onClick={() => setShowCalendar(false)}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.3)",
              zIndex: 1000,
            }}
          />
          <div
            style={{
              position: "fixed",
              bottom: 0,
              /* full-width 꽉 채우기 + 자동 가운데 정렬 */
              left: 0,
              right: 0,
              margin: "0 auto",
              maxWidth: 393,
              boxSizing: "border-box",
              background: C.card,
              borderTopLeftRadius: 20,
              borderTopRightRadius: 20,
              /* 좌우 패딩만 조금 줄여주세요 */
              padding: "24px 16px",
              zIndex: 1001,
              /* 혹시 몰라서 수평 스크롤 숨기기 */
              overflowX: "hidden",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 16,
              }}
            >
              <button
                onClick={prevMonth}
                style={{ background: "none", border: "none", fontSize: 24 }}
              >
                <FiChevronLeft />
              </button>
              <span style={{ fontSize: 18, fontWeight: 600 }}>
                {currentYear}년 {currentMonth + 1}월
              </span>
              <button
                onClick={nextMonth}
                style={{ background: "none", border: "none", fontSize: 24 }}
              >
                <FiChevronRight />
              </button>
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(7,1fr)",
                marginBottom: 8,
                textAlign: "center",
                fontWeight: 600,
              }}
            >
              {weekDays.map((d, i) => (
                   <div key={d}
                     style={{
                       color:
                        i === 0
                           ? C.primary          // 일요일은 primary 컬러
                           : i === 6
                           ? C.primary          // 토요일도 primary 컬러
                           : C.text             // 그 외는 기본 text 컬러
                     }}>
                  {d}
                </div>
              ))}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 6 }}>
              {calendar.flatMap((w, r) =>
                w.map((cell, c) => {
                  const isSel = cell.offset === 0 && cell.day === selectedDate;
                  return (
                    <button
                      key={`${r}-${c}`}
                      disabled={!cell}
                      onClick={() => handleDateClick(cell)}
                      style={{
                        width: 44,
                        height: 44,
                        borderRadius: 8,
                        border: isSel ? "1.5px solid #333" : "none",
                        background: isSel ? "transparent" : "#F9F9FA",
                        color: cell && cell.offset === 0 ? "#333" : "#C7C7CC",
                        position: "relative",
                      }}
                    >
                      {isSel && (
                        <span
                          style={{
                            position: "absolute",
                            inset: 4,
                            borderRadius: "50%",
                            background: "#DBD1BD",
                            zIndex: -1,
                          }}
                        />
                      )}
                     {cell?.day || ""}
                    </button>
                  );
                })
              )}
            </div>
            <button
              onClick={() => setShowCalendar(false)}
              style={{
                marginTop: 24,
                width: "100%",
                padding: 12,
                borderRadius: 8,
                border: "none",
                background: "#DBD1BD",
                fontWeight: 700,
              }}
            >
              닫기
            </button>
          </div>
        </>
      )}
    </div>
  );
}